if (!customElements.get('bfr-video-modal')) {
  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return '0:00';
    const whole = Math.floor(seconds);
    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
  };

  class BfrVideoModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.player = this.querySelector('[data-bfr-player]');
      this.seek = this.querySelector('[data-bfr-seek]');
      this.fill = this.querySelector('[data-bfr-fill]');
      this.current = this.querySelector('[data-bfr-current]');
      this.duration = this.querySelector('[data-bfr-duration]');
      this.playButtons = this.querySelectorAll('[data-bfr-play]');
      this.muteButton = this.querySelector('[data-bfr-mute]');
      this.frame = 0;
      this.idleTimer = 0;

      this.querySelector('[data-bfr-video-open]').addEventListener('click', () => this.open());
      this.querySelector('[data-bfr-video-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => this.onClose());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event));
      this.playButtons.forEach((button) => button.addEventListener('click', () => this.togglePlay()));
      this.muteButton.addEventListener('click', () => this.toggleMute());
      this.querySelector('[data-bfr-fullscreen]').addEventListener('click', () => this.toggleFullscreen());
      this.seek.addEventListener('input', () => this.onSeek());
      this.player.addEventListener('pointermove', () => this.wake());
      this.player.addEventListener('focusin', () => this.wake());
    }

    open() {
      this.mountVideo();
      this.dialog.showModal();
      document.body.classList.add('overflow-hidden');
      this.video.play().catch(() => this.render());
      this.wake();
    }

    onClose() {
      document.body.classList.remove('overflow-hidden');
      if (this.video) this.video.pause();
      if (document.fullscreenElement) document.exitFullscreen();
    }

    mountVideo() {
      if (this.video) return;
      const template = this.querySelector('[data-bfr-video-template]');
      this.player.prepend(template.content.cloneNode(true));
      this.video = this.player.querySelector('video');

      ['play', 'pause', 'ended', 'volumechange'].forEach((type) =>
        this.video.addEventListener(type, () => this.render())
      );
      this.video.addEventListener('loadedmetadata', () => this.renderTime());
      this.video.addEventListener('timeupdate', () => {
        if (this.video.paused) this.renderTime();
      });
    }

    togglePlay() {
      if (this.video.paused || this.video.ended) {
        this.video.play();
      } else {
        this.video.pause();
      }
    }

    toggleMute() {
      this.video.muted = !this.video.muted;
    }

    toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (this.player.requestFullscreen) {
        this.player.requestFullscreen();
      } else if (this.video.webkitEnterFullscreen) {
        this.video.webkitEnterFullscreen();
      }
    }

    onSeek() {
      if (!this.video.duration) return;
      this.video.currentTime = (this.seek.value / 1000) * this.video.duration;
      this.renderTime();
    }

    onKeydown(event) {
      if (!this.video || event.target.matches('input')) return;
      const keys = {
        ' ': () => this.togglePlay(),
        k: () => this.togglePlay(),
        m: () => this.toggleMute(),
        f: () => this.toggleFullscreen(),
        ArrowRight: () => (this.video.currentTime += 5),
        ArrowLeft: () => (this.video.currentTime -= 5),
      };
      const action = keys[event.key];
      if (!action || (event.key === ' ' && event.target.matches('button'))) return;
      event.preventDefault();
      action();
      this.wake();
    }

    wake() {
      this.player.classList.remove('is-idle');
      clearTimeout(this.idleTimer);
      if (this.video && !this.video.paused) {
        this.idleTimer = setTimeout(() => this.player.classList.add('is-idle'), 2500);
      }
    }

    render() {
      const playing = !this.video.paused && !this.video.ended;
      this.player.classList.toggle('is-playing', playing);
      this.player.classList.toggle('is-muted', this.video.muted);
      this.playButtons.forEach((button) => {
        button.setAttribute('aria-label', playing ? button.dataset.labelPause : button.dataset.labelPlay);
      });
      this.muteButton.setAttribute(
        'aria-label',
        this.video.muted ? this.muteButton.dataset.labelUnmute : this.muteButton.dataset.labelMute
      );

      cancelAnimationFrame(this.frame);
      if (playing) this.tick();
      this.renderTime();
      this.wake();
    }

    tick() {
      this.renderTime();
      this.frame = requestAnimationFrame(() => this.tick());
    }

    renderTime() {
      const { currentTime, duration } = this.video;
      const progress = duration ? currentTime / duration : 0;
      this.fill.style.transform = `scaleX(${progress})`;
      this.seek.value = Math.round(progress * 1000);
      this.current.textContent = formatTime(currentTime);
      this.duration.textContent = formatTime(duration);
    }
  }

  customElements.define('bfr-video-modal', BfrVideoModal);
}
